import React, { useEffect, useRef, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { getSiteAdminTemplatesRoute, getSiteAdminTemplateCategoriesRoute, updateTemplateFlagsRoute } from '../../../api/routes';
import { ReactComponent as PencilIcon } from '../../../assets/icons/Pencil.svg';
import config from '../../../config';
import LottieIcon from '../../../components/common/LottieIcon';
import '../SiteAdmin.scss';

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

// Group templates: campaign templates go under 'Campaign', feature templates group by featureType.
// Within each group, sort by category name then by template name.
const groupByFeatureType = (templates, categoryMap = {}) => {
    const groups = {};
    templates.forEach(t => {
        const key = t.templateType === 'campaign' ? 'Campaign' : (t.featureType || 'Uncategorised');
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
    });
    return Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([groupName, groupTemplates]) => [
            groupName,
            [...groupTemplates].sort((a, b) => {
                const catA = (categoryMap[a.categoryId] || '').toLowerCase();
                const catB = (categoryMap[b.categoryId] || '').toLowerCase();
                if (catA < catB) return -1;
                if (catA > catB) return 1;
                return (a.name || '').localeCompare(b.name || '');
            }),
        ]);
};

// ── Toggle component ───────────────────────────────────────────────────────────

const FlagToggle = ({ label, value, onChange, saving }) => (
    <div className="tm-flag" onClick={() => !saving && onChange(!value)}>
        <div className={`tm-flag-toggle${value ? ' on' : ''}`} />
        <span>{label}</span>
    </div>
);

// ── Template row ───────────────────────────────────────────────────────────────

const TemplateRow = ({ template, categoryMap, onFlagChange, onEdit }) => {
    const [saving, setSaving] = useState(false);
    const [descOverflows, setDescOverflows] = useState(false);
    const descTextRef = useRef(null);

    const handleFlag = async (flag, value) => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${updateTemplateFlagsRoute}/${template.id}/flags`,
                { [flag]: value },
                { withCredentials: true }
            );
            if (res.data.success) {
                onFlagChange(template.id, { [flag]: value });
            }
        } catch {
            toast.error('Failed to update template');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="template-manager-row">
            <div
                className="tm-thumb"
                style={template.templateImage ? { backgroundImage: `url(${toDisplayUrl(template.templateImage)})` } : {}}
            />
            <div className="tm-info">
                <div className="tm-name">{template.name}</div>
                <div className="tm-type">
                    {template.templateType === 'campaign' ? 'Campaign' : template.featureType}
                    {' '}
                    <span className={`tcm-status-badge${template.isActive === true ? ' active' : ' inactive'}`}>
                        {template.isActive === true ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>
            <div className="tm-category">
                {template.categoryId && categoryMap[template.categoryId]
                    ? <span className="tm-category-label">{categoryMap[template.categoryId]}</span>
                    : <span className="tm-category-none">—</span>}
            </div>
            <div className="tm-desc" onMouseEnter={() => {
                if (descTextRef.current) setDescOverflows(descTextRef.current.scrollHeight > descTextRef.current.clientHeight);
            }}>
                <span className="tm-desc-text" ref={descTextRef}>{template.description || ''}</span>
                {template.description && descOverflows && (
                    <div className="tm-desc-tooltip">{template.description}</div>
                )}
            </div>
            <div className="tm-feature-icons">
                {(template.features || []).map((f, i) => (
                    f.isStepFeature ? (
                        <div key={i} className={`tm-fi-main category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={26} />
                        </div>
                    ) : (
                        <div key={i} className={`tm-fi-disruptor category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={13} />
                        </div>
                    )
                ))}
            </div>
            <div className="tm-flags">
                <FlagToggle
                    label="Active"
                    value={!!template.isActive}
                    saving={saving}
                    onChange={v => handleFlag('isActive', v)}
                />
                <FlagToggle
                    label="Quickstart"
                    value={!!template.isQuickstart}
                    saving={saving}
                    onChange={v => handleFlag('isQuickstart', v)}
                />
                <FlagToggle
                    label="Featured"
                    value={!!template.isFeatured}
                    saving={saving}
                    onChange={v => handleFlag('isFeatured', v)}
                />
            </div>
            <div className="tm-edit-icon" onClick={() => onEdit(template)} title="Edit template">
                <PencilIcon className="tm-pencil" aria-label="Edit" />
            </div>
        </div>
    );
};

// ── Main TemplateManager ───────────────────────────────────────────────────────

const TemplateManager = ({ onEdit, onCreate, templatePatch, onTemplatesLoaded, onFlagChange: onFlagChangeExternal }) => {
    const [templates, setTemplates] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [filterQuickstart, setFilterQuickstart] = useState('all');
    const [filterFeatured, setFilterFeatured] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');

    useEffect(() => {
        if (templatePatch) {
            setTemplates(prev => prev.map(t => t.id === templatePatch.id ? { ...t, ...templatePatch } : t));
        }
    }, [templatePatch]);

    useEffect(() => {
        Promise.all([
            axios.get(getSiteAdminTemplatesRoute, { withCredentials: true }),
            axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true }),
        ])
            .then(([tRes, cRes]) => {
                if (tRes.data.success) {
                    setTemplates(tRes.data.templates);
                    if (onTemplatesLoaded) onTemplatesLoaded(tRes.data.templates);
                }
                if (cRes.data.success) {
                    const map = {};
                    (cRes.data.categories || []).forEach(c => { map[c.id] = c.name; });
                    setCategoryMap(map);
                }
            })
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFlagChange = (id, patch) => {
        setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
        if (onFlagChangeExternal) onFlagChangeExternal(id, patch);
    };

    let filtered = search
        ? templates.filter(t =>
            t.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.featureType?.toLowerCase().includes(search.toLowerCase())
          )
        : templates;
    if (filterActive === 'active') filtered = filtered.filter(t => t.isActive === true);
    if (filterActive === 'inactive') filtered = filtered.filter(t => t.isActive !== true);
    if (filterQuickstart === 'yes') filtered = filtered.filter(t => t.isQuickstart);
    if (filterQuickstart === 'no') filtered = filtered.filter(t => !t.isQuickstart);
    if (filterFeatured === 'yes') filtered = filtered.filter(t => t.isFeatured);
    if (filterFeatured === 'no') filtered = filtered.filter(t => !t.isFeatured);
    if (filterCategory === 'none') filtered = filtered.filter(t => !t.categoryId);
    else if (filterCategory !== 'all') filtered = filtered.filter(t => t.categoryId === filterCategory);

    const groups = groupByFeatureType(filtered, categoryMap);

    // Sorted category list for the dropdown
    const categoryOptions = Object.entries(categoryMap).sort(([, a], [, b]) => a.localeCompare(b));

    if (loading) return <div className="home-loading-placeholder" />;

    return (
        <div>
            {onCreate && (
                <div style={{ marginBottom: '1rem', width: "150px" }}>
                    <div className="standard-button button-active" onClick={onCreate}>+ Create Template</div>
                </div>
            )}
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <input
                    className="sa-text-input"
                    style={{ maxWidth: '320px' }}
                    placeholder="Search templates…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    className="sa-select sa-select--narrow"
                    value={filterActive}
                    onChange={e => setFilterActive(e.target.value)}
                >
                    <option value="all">All Statuses</option>
                    <option value="active">Active Only</option>
                    <option value="inactive">Inactive Only</option>
                </select>
                <select
                    className="sa-select sa-select--narrow"
                    value={filterQuickstart}
                    onChange={e => setFilterQuickstart(e.target.value)}
                >
                    <option value="all">All Quickstart</option>
                    <option value="yes">Quickstart Only</option>
                    <option value="no">Not Quickstart</option>
                </select>
                <select
                    className="sa-select sa-select--narrow"
                    value={filterFeatured}
                    onChange={e => setFilterFeatured(e.target.value)}
                >
                    <option value="all">All Featured</option>
                    <option value="yes">Featured Only</option>
                    <option value="no">Not Featured</option>
                </select>
                <select
                    className="sa-select sa-select--narrow"
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                >
                    <option value="all">All Categories</option>
                    {categoryOptions.map(([id, name]) => (
                        <option key={id} value={id}>{name}</option>
                    ))}
                </select>
                <span style={{ fontSize: '0.8rem', color: 'var(--label-color)' }}>
                    {filtered.length} template{filtered.length !== 1 ? 's' : ''}
                    {' · '}
                    {filtered.filter(t => t.isQuickstart).length} quickstart
                    {' · '}
                    {filtered.filter(t => t.isFeatured).length} featured
                    {filtered.length !== templates.length && (
                        <>{' · '}out of {templates.length} total</>
                    )}
                </span>
            </div>

            <div className="template-manager-grid">
                {groups.map(([groupName, groupTemplates]) => (
                    <div key={groupName}>
                        <div className="template-manager-group-title">{groupName}</div>
                        {groupTemplates.map(t => (
                            <TemplateRow key={t.id} template={t} categoryMap={categoryMap} onFlagChange={handleFlagChange} onEdit={onEdit} />
                        ))}
                    </div>
                ))}

                {groups.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--label-color)' }}>
                        No templates found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateManager;
